/**
 * Stricter email format validation than Angular's `Validators.email`.
 *
 * Angular's built-in email validator accepts addresses that bounce in
 * practice (e.g. `foo@bar` without a TLD). This check is deliberately
 * conservative: it rejects clearly malformed input while still accepting
 * any plausibly deliverable address. It does NOT verify deliverability
 * (no MX/DNS lookup) and is not a full RFC 5322 parser — that level of
 * strictness rejects valid real-world addresses and is not the goal here.
 *
 * Rejected examples:
 *   - missing TLD:            user@localhost, user@bar
 *   - empty local/domain:     @gmail.com, user@
 *   - whitespace:             "us er@gmail.com"
 *   - multiple @:             a@b@c.de
 *   - leading/trailing dot:   .user@gmail.com, user@gmail.com.
 *   - consecutive dots:       user@gmail..com, us..er@gmail.com
 *   - too-short TLD:          user@gmail.c
 */

// One @, non-empty local part (no spaces/@), domain of dot-separated
// labels, final label (TLD) at least two letters.
const EMAIL_FORMAT =
  /^[^\s@]+@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;

/**
 * Returns true if the input is a plausibly valid email address.
 *
 * Empty / null / undefined returns false. Input is trimmed before checking
 * (a value that is only valid after trimming whitespace is considered valid
 * — match Angular form semantics where the control value is what gets sent).
 */
export function isValidEmailFormat(
  input: string | null | undefined,
): boolean {
  if (!input) return false;

  const trimmed = input.trim();
  if (!trimmed) return false;

  // Guard against consecutive dots anywhere (regex above allows them
  // within a label run, so reject explicitly).
  if (trimmed.includes('..')) return false;

  // Local part must not start or end with a dot.
  const atIdx = trimmed.indexOf('@');
  if (atIdx <= 0) return false;
  const local = trimmed.slice(0, atIdx);
  if (local.startsWith('.') || local.endsWith('.')) return false;

  return EMAIL_FORMAT.test(trimmed);
}
